import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/DateInput";
import { api, errMsg } from "@/lib/api";
import { dmyToISO, isoToDMY } from "@/lib/seat";
import { DETAIL_FIELDS } from "@/components/seat/ProductDetail";

const SHORT = [["name", "Nama produk"], ["group", "Maskapai / Charter (grup)"], ["departure_city", "Kota keberangkatan"], ["days", "Durasi (contoh 7D)"], ["operator", "Operator"]];
const ALL_TEXT = [...SHORT.map(([k]) => k), ...DETAIL_FIELDS.map(([k]) => k), "itinerary_regular", "itinerary_promo"];
const blank = () => ({ ...Object.fromEntries(ALL_TEXT.map((k) => [k, ""])), departure_city: "Jakarta", template_ids: [], promos: [] });

const PromoRow = ({ x, onChange, onRemove, i }) => (
  <div className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center">
    <Input value={x.note} onChange={(e) => onChange({ note: e.target.value })} placeholder="Contoh: Turun harga 1 juta" data-testid={`seat-promo-note-${i}`} />
    <DateInput value={x.start} onChange={(v) => onChange({ start: v })} className="h-10 w-36 text-sm" data-testid={`seat-promo-start-${i}`} />
    <DateInput value={x.end} onChange={(v) => onChange({ end: v })} className="h-10 w-36 text-sm" data-testid={`seat-promo-end-${i}`} />
    <button type="button" onClick={onRemove} className="justify-self-end rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Hapus promo"><X className="h-4 w-4" /></button>
  </div>
);

const TemplatePicker = ({ templates, value, onChange }) => {
  const [q, setQ] = useState("");
  const list = templates.filter((t) => t.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-md border border-slate-200">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari template..." className="rounded-b-none border-0 border-b" data-testid="seat-template-search" />
      <div className="max-h-44 overflow-y-auto p-2">
        {list.length === 0 && <p className="px-2 py-1 text-xs text-slate-400">Tidak ada template.</p>}
        {list.map((t) => (
          <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
            <Checkbox checked={value.includes(t.id)} onCheckedChange={(c) => onChange(c ? [...value, t.id] : value.filter((x) => x !== t.id))} data-testid={`seat-template-option-${t.id}`} />
            {t.title}
          </label>
        ))}
      </div>
    </div>
  );
};

export const ProductDialog = ({ state, templates, onClose, onSaved }) => {
  const { open, product } = state;
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  useEffect(() => {
    if (open) setForm(product ? { ...blank(), ...Object.fromEntries(ALL_TEXT.map((k) => [k, product[k] || ""])), template_ids: product.template_ids || [], promos: (product.promos || []).map((x) => ({ note: x.note, start: isoToDMY(x.start), end: isoToDMY(x.end) })) } : blank());
  }, [open, product]);

  const submit = async (e) => {
    e.preventDefault();
    const promos = [];
    for (const x of form.promos.filter((p) => p.note.trim())) {
      const start = x.start ? dmyToISO(x.start) : null, end = x.end ? dmyToISO(x.end) : null;
      if ((x.start && !start) || (x.end && !end)) return toast.error(`Tanggal promo "${x.note}" tidak valid`);
      promos.push({ note: x.note, start, end });
    }
    setBusy(true);
    try {
      const body = { ...form, promos };
      if (product) await api.put(`/seat/products/${product.id}`, body); else await api.post("/seat/products", body);
      toast.success(product ? "Produk diperbarui" : "Produk ditambahkan");
      onClose(); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const updPromo = (i, p) => set({ promos: form.promos.map((x, j) => (j === i ? { ...x, ...p } : x)) });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="seat-product-dialog">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">{product ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {SHORT.map(([k, label]) => (
              <div key={k} className={`space-y-2 ${k === "name" ? "sm:col-span-2" : ""}`}>
                <Label>{label}</Label>
                <Input value={form[k]} onChange={(e) => set({ [k]: e.target.value })} required={k === "name"} data-testid={`seat-form-${k}`} />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Remark promo / turun harga</Label>
              <button type="button" onClick={() => set({ promos: [...form.promos, { note: "", start: "", end: "" }] })} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-testid="seat-add-promo"><Plus className="h-3.5 w-3.5" /> Tambah remark</button>
            </div>
            <p className="text-xs text-slate-500">Isi tanggal mulai & akhir (DD MM YYYY). Remark tampil otomatis selama periode berlaku.</p>
            {form.promos.map((x, i) => <PromoRow key={i} i={i} x={x} onChange={(p) => updPromo(i, p)} onRemove={() => set({ promos: form.promos.filter((_, j) => j !== i) })} />)}
          </div>
          <div className="space-y-2"><Label>Balasan Cepat terkait</Label><TemplatePicker templates={templates} value={form.template_ids} onChange={(v) => set({ template_ids: v })} /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[...DETAIL_FIELDS, ["itinerary_regular", "Itinerary Reguler (teks WhatsApp)"], ["itinerary_promo", "Itinerary Promo (teks WhatsApp)"]].map(([k, label]) => (
              <div key={k} className="space-y-2"><Label>{label}</Label>
                <Textarea value={form[k]} onChange={(e) => set({ [k]: e.target.value })} rows={k.startsWith("itinerary") ? 8 : 3} data-testid={`seat-form-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid="seat-form-submit">{busy ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
